import { Alert } from '@ordo/ui/alert'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Label } from '@ordo/ui/label'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { Switch } from '@ordo/ui/switch'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { useContactSubscriptions, useUpdateContactGlobalUnsubscribe, useUpdateContactSubscription } from 'hooks/api'
import { ShieldAlert } from 'lucide-react'
import { useCallback } from 'react'

import { ContactSmsPreferences } from './contact-sms-preferences'

export function ContactSubscriptions({ contactId }: { contactId: string }) {
  const { data, loading, refetch } = useContactSubscriptions(contactId)
  const { perform: updateSubscription } = useUpdateContactSubscription()
  const { perform: updateGlobal } = useUpdateContactGlobalUnsubscribe()

  const handleTopicToggle = useCallback(
    async (topicId: string, subscribed: boolean) => {
      await updateSubscription(contactId, { topicId, optedOut: !subscribed })
      refetch()
    },
    [contactId, updateSubscription, refetch],
  )

  const handleGlobalToggle = useCallback(
    async (subscribed: boolean) => {
      await updateGlobal(contactId, !subscribed)
      refetch()
    },
    [contactId, updateGlobal, refetch],
  )

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-medium text-muted-foreground">Subscriptions</h3>
        <p className="text-xs text-muted-foreground">
          Topic preferences govern scheduled messages. SMS STOP preferences also apply to transactional messages.
        </p>
      </div>

      <LoadingWrapper loading={loading} loadingElement={<Skeleton className="h-24 w-full" />}>
        {data && (
          <>
            <ContactSmsPreferences
              smsStoppedAt={data.smsStoppedAt ?? null}
              smsSenderStops={data.smsSenderStops ?? []}
            />
            <Separator />
            {data.unsubscribedAt && (
              <Alert variant="error">
                <div className="flex items-start gap-2">
                  <ShieldAlert className="mt-0.5 h-4 w-4" />
                  <div>
                    <div className="font-medium">Globally unsubscribed</div>
                    <p className="mt-1 text-sm">
                      This contact is opted out of all marketing emails since{' '}
                      {new Date(data.unsubscribedAt).toLocaleString()}. Per-topic toggles below are overridden until you
                      re-subscribe them globally.
                    </p>
                  </div>
                </div>
              </Alert>
            )}

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="text-sm font-medium">Receive marketing emails</Label>
                <p className="text-xs text-muted-foreground">
                  Master switch. When off, no scheduled communications will be sent.
                </p>
              </div>
              <Switch checked={data.unsubscribedAt === null} onCheckedChange={(v) => handleGlobalToggle(Boolean(v))} />
            </div>

            <Separator />

            {data.subscriptions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No topics defined yet. Create subscription topics in Settings.
              </p>
            ) : null}

            {data.subscriptions.map((sub) => {
              const subscribed = sub.optedOutAt === null
              return (
                <div key={sub.topicId} className="flex items-start justify-between gap-4 rounded-md border p-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{sub.topicName}</span>
                      {sub.isDefault && <Badge variant="secondary">Default</Badge>}
                    </div>
                    {sub.topicDescription && (
                      <p className="mt-1 text-xs text-muted-foreground">{sub.topicDescription}</p>
                    )}
                    {sub.optedOutAt && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Opted out {new Date(sub.optedOutAt).toLocaleString()}
                        {sub.optedOutReason ? ` (${sub.optedOutReason})` : ''}
                      </p>
                    )}
                  </div>
                  <Switch
                    checked={subscribed}
                    onCheckedChange={(v) => handleTopicToggle(sub.topicId, Boolean(v))}
                    disabled={data.unsubscribedAt !== null}
                  />
                </div>
              )
            })}

            {data.unsubscribedAt && (
              <Button variant="outline" size="sm" onClick={() => handleGlobalToggle(true)} className="w-full">
                Re-subscribe to all marketing emails
              </Button>
            )}
          </>
        )}
      </LoadingWrapper>
    </div>
  )
}
